import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import md5 from 'crypto-js/md5';

import { Provider } from './StateContext';
import routes from '../../../routes/routes';

// Используем объект вместо массива для хранения контроллеров
const reactControllers = {};

const StateProvider = (props) => {
  const { children } = props;

  const [showLoading, setShowLoading] = useState(true);

  const jobResultTimer = useRef(null);

  const sendRequest = (route, options) => {
    const opts = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      // mimeType: 'multipart/form-data',
      credentials: 'include',
      ...(options || {}),
    };
    const { body } = opts;
    const jobsCount = body && Array.isArray(body.jobs) ? body.jobs.length : undefined;
    if (window.localStorage && window.localStorage.getItem('cabinet-token')) {
      opts.headers.Authorization = `Bearer ${window.localStorage.getItem('cabinet-token')}`;
    }
    if (window.AccessToken) {
      opts.headers['Access-Token'] = window.AccessToken;
    }

    if (opts.body) {
      if (opts.headers['Content-Type'] === 'application/json') {
        opts.body = JSON.stringify(opts.body);
      } else if (typeof opts.body === 'object' && opts.headers.type !== 'formData') {
        opts.body = new URLSearchParams(opts.body);
      }
      if (opts.headers['Content-Type'] === 'multipart/form-data') {
        delete opts.headers['Content-Type'];
        delete opts.headers['content-type'];
      }
    }

    const requestUrl = route in routes
      ? routes[route] + (route === 'downloadInvoice' ? `id/${body.id}/` : '')
      : (typeof route === 'string' && /^https?:\/\//.test(route) ? route : null);

    if (requestUrl) {
      const isPollingRoute = ['jobsResult', 'jobResult'].indexOf(route) !== -1;
      const skipBodyHash = isPollingRoute || route === 'translations';
      const index = typeof opts.body === 'string'
      && !skipBodyHash
      && route.slice(0, 6) !== 'report' && route.slice(0, 5) !== 'smsBy'
        ? `${route}-${md5(opts.body).toString()}` : route;

      // jobsResult/jobResult могут идти параллельно, предыдущий не отменяем
      const allowConcurrent = ['jobsResult', 'jobResult'].indexOf(route) !== -1;
      const controller = new AbortController();
      const requestId = `${route}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      let timeoutId = null;

      if (!allowConcurrent) {
        if (index in reactControllers) {
          reactControllers[index].abort();
        }
        reactControllers[index] = controller;
      }

      opts.signal = controller.signal;
      opts.headers['X-Request-ID'] = requestId;

      if (isPollingRoute) {
        timeoutId = setTimeout(() => {
          console.warn(`[${route}] Request timeout after 15s`, {
            url: requestUrl,
            requestId,
            jobsCount,
          });
          controller.abort();
        }, 15000);
      }

      const cleanupRequest = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (!allowConcurrent && reactControllers[index] === controller) {
          delete reactControllers[index];
        }
      };

      return fetch(requestUrl, opts).then((response) => {
        if (!response.ok && [403, 422].indexOf(response.status) === -1) {
          return {
            errors: {
              route: `Route "${route}" no ok`,
              status: response.status,
            },
          };
        }

        // const version = response.headers.get('X-Version');
        // const balance = response.headers.get('X-Balance'); TODO need after remove angular
        // const credit = response.headers.get('X-Credit');

        // if (version) {
        //   window.localStorage.setItem('ls.version', version);
        // }

        if (response.url.includes('/downloadInvoice/') || response.url.includes('/statementDownload/')) {
          const downloadInvoice = async (res) => {
            const contentDisposition = res.headers.get('Content-Disposition');
            const match = contentDisposition ? contentDisposition.match(/filename="([^"]+)"/) : [];
            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(await res.blob());
            a.setAttribute('download', match[1] || 'download');
            a.click();
          };

          return downloadInvoice(response);
        }

        if (response.url.includes('/export/')
          || response.url.includes('/arrayToFile/')
          || response.url.includes('/balanceHistoryExport/')
          || response.url.includes('/viberTemplatesExport/')
          || response.url.includes('/contractFileDownload/')
          || response.url.includes('/download/')
          || response.url.includes('/exportPhones/')
          || response.url.includes('/reportPhonebookCleaner/')) {
          return response.blob();
        }

        return response.text().then((text) => {
          try {
            return JSON.parse(text);
          } catch (e) {
            // console.log(text);
            return text;
          }
        });
      }).catch((error) => ({
        errors: {
          route: error,
        },
      })).finally(() => {
        cleanupRequest();
      });
    }

    // console.error(`Route "${route}" does not exists in routes config`);

    return Promise.resolve({
      errors: {
        route: `Route "${route}" does not exists in routes config`,
      },
    });
  };

  const abortRequest = (route) => {
    if (route in reactControllers) {
      reactControllers[route].abort();
    }
  };

  const jobResult = (job, callbackSuccess, callbackProcess, callbackError) => {
    clearTimeout(jobResultTimer.current);

    sendRequest('jobResult', {
      body: {
        job,
      },
    }).then((response) => {
      if (response && response.success) {
        const statusId = Number(response.data.status_id);

        if (statusId === 4) { // success
          if (typeof callbackSuccess === 'function') {
            callbackSuccess(response.data.result);
          }
        } else if (statusId === 1 || statusId === 2) { // waiting / in process
          if (statusId === 2 && typeof callbackProcess === 'function') {
            callbackProcess(response.data.percent, response.data.status_text || '');
          }

          jobResultTimer.current = setTimeout(
            jobResult,
            2000,
            job,
            callbackSuccess,
            callbackProcess,
            callbackError,
          );
        } else if (typeof callbackError === 'function') { // failed, missing, or dead
          callbackError();
        }
      } else {
        const routeError = response && response.errors && response.errors.route;
        const isNetworkError = routeError instanceof Error
          || (routeError && routeError.name === 'AbortError');

        if (isNetworkError) {
          jobResultTimer.current = setTimeout(
            jobResult,
            2000,
            job,
            callbackSuccess,
            callbackProcess,
            callbackError,
          );
        } else if (typeof callbackError === 'function') {
          callbackError();
        }
      }
    });
  };

  const currentProps = {
    value: {
      showLoading,
      setShowLoading,
      sendRequest,
      abortRequest,
      jobResult,
    },
  };

  return (
    <Provider {...currentProps}>{children}</Provider>
  );
};

StateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default StateProvider;
